import classNames from "classnames";
import { html, TemplateResult } from "lit-html";
import i18next from "i18next";
import { useRef } from "haunted";
import { useEffectWithoutCleanup, useMemo, useState } from "Shared/haunted/CustomHooks";
import { ref } from "Components/directives/ref";
import {
	CHILE_COUNTRY_CODE,
	COLOMBIA_COUNTRY_CODE,
	COLOMBIAN_CULTURE_CODE,
	MIN_NUMBER_FOR_GROUP_BOOKINGS,
} from "Services/constants";
import { useReduxState } from "Shared/redux/useReduxState";
import { CLASS_NAMES } from "Services/ClassNames";
import { SearchPassengers } from "./CurrentSearch";
import { unsafeHTML } from "lit-html/directives/unsafe-html";

type PaxType = "adults" | "teens" | "children" | "infants";

type WarningKey =
	| "groupBooking"
	| "teenInfantRemove"
	| "minorOrInfantAge"
	| "childrenAgeWarningColombia"
	| "teenLegalGuardian";

export interface Props {
	culture: string;
}

export const usePassengerSelector = (props: Props) => {
	const [currentSearch, setCurrentSearch] = useReduxState("currentSearch");
	const { adults, teens, children, infants } = currentSearch.passengers;

	const root = useRef<HTMLDivElement>(null);
	const dropdownElem = useRef<HTMLDivElement>(null);

	const [isOpen, setIsOpen] = useState(false);
	const [isWithGroupBookingWarning, setIsWithGroupBookingWarning] = useState(false);
	const [isWithTeenInfantRemoveWarning, setIsWithTeenInfantRemoveWarning] = useState(false);
	const [warningKeys, setWarningKeys] = useState<WarningKey[]>([]);

	const total = adults + teens + children + infants;

	const isColombianDomesticFlight = useMemo(
		() =>
			currentSearch.originCity?.countryCode === COLOMBIA_COUNTRY_CODE &&
			currentSearch.destinationCity?.countryCode === COLOMBIA_COUNTRY_CODE,
		[currentSearch.originCity?.countryCode, currentSearch.destinationCity?.countryCode]
	);

	const maxChildrenAmount = (passengers: SearchPassengers) => {
		if (passengers.adults > 0) return Number.POSITIVE_INFINITY;

		const retVal = passengers.teens - passengers.infants;

		return retVal > 0 ? retVal : 0;
	};

	const maxInfantAmount = (passengers: SearchPassengers) => {
		if (passengers.adults > 0) return passengers.adults + passengers.teens;

		const retVal = passengers.teens - passengers.children;

		return retVal > 0 ? retVal : 0;
	};

	const isChileanSearch = () =>
		currentSearch?.originCity?.countryCode === CHILE_COUNTRY_CODE ||
		currentSearch?.destinationCity?.countryCode === CHILE_COUNTRY_CODE;

	const isAtGroupLimit = () => adults + teens + children === MIN_NUMBER_FOR_GROUP_BOOKINGS - 1;
	const isAddAdultsDisabled = () => isAtGroupLimit();
	const isAddTeensDisabled = () => isAtGroupLimit();
	const isAddChildrenDisabled = () => children >= maxChildrenAmount(currentSearch.passengers) || isAtGroupLimit();
	const isRemoveAdultsDisabled = () => (adults === 1 && teens === 0) || adults === 0;
	const isRemoveTeensDisabled = () => (adults === 0 && teens === 1) || teens === 0;
	const isRemoveChildrenDisabled = () => children === 0;
	const isAddInfantsDisabled = () => infants >= maxInfantAmount(currentSearch.passengers);
	const isRemoveInfantsDisabled = () => infants === 0;

	const getGroupBookingWarning = () => {
		const [language, country] = props.culture.toLowerCase().split("-");
		const groupUrl = `/${country}/${language}/minisitios/grupos`;

		return html`
			<div>
				${i18next.t("search-box-group-warning")}<a
					href="${groupUrl}"
					class="ml-1 text-n-red underline md:hover:no-underline"
					>${i18next.t("search-box-group-warning-2")}</a
				>
			</div>
		`;
	};

	const getTeenInfantRemoveWarning = () =>
		html`${unsafeHTML(
			i18next.t(
				"Se ha eliminado un infante de tu búsqueda, ya que los pasajeros entre {{-interval}} años no están habilitados a volar con más de un infante/niño a cargo",
				{
					interval: isChileanSearch() ? "14-17" : "13-17",
				}
			)
		)}`;

	const getTeenLegalGuardianWarning = () =>
		html`${unsafeHTML(
			i18next.t(
				"Recuerda que si viaja un niño y un adolescente ({{-interval}} años), este último debe ser su padre/madre o tutor legal",
				{
					interval: isChileanSearch() ? "14-17" : "13-17",
				}
			)
		)}`;

	const warningsMap = new Map<WarningKey, TemplateResult>([
		["groupBooking", getGroupBookingWarning()],
		["teenInfantRemove", getTeenInfantRemoveWarning()],
		["teenLegalGuardian", getTeenLegalGuardianWarning()],
		[
			"minorOrInfantAge",
			html`${i18next.t("Recuerda que la edad del niño o infante se considera al momento de terminar el viaje")}`,
		],
		[
			"childrenAgeWarningColombia",
			html`${i18next.t(
				"En base a la ley colombiana, el descuento en la tarifa sólo aplica para los menores de 12 años"
			)}`,
		],
	]);

	const init = () => {
		const onClickOutside = (e: any) => {
			const path = e.path || (e.composedPath && e.composedPath());
			const isClickInside = path.indexOf(dropdownElem.current) > -1 || path.indexOf(root.current) > -1;

			if (!isClickInside) setIsOpen(false);
		};

		const onScroll = () => setIsOpen(false);

		document.addEventListener("click", onClickOutside);
		document.addEventListener("scroll", onScroll);

		return () => {
			document.removeEventListener("click", onClickOutside);
			document.removeEventListener("scroll", onScroll);
		};
	};

	const checkForGroupBookingWarning = (type: PaxType) => {
		if (type !== "infants" && isAtGroupLimit()) {
			setIsWithGroupBookingWarning(true);
			return;
		}

		setIsWithGroupBookingWarning(false);
	};

	const checkForTeenInfantRemoveWarning = (type: PaxType) => {
		if (type !== "adults" && type !== "teens") {
			setIsWithTeenInfantRemoveWarning(false);
			return;
		}

		const adultsNew = type === "adults" ? adults - 1 : adults;
		const teensNew = type === "teens" ? teens - 1 : teens;

		const newPassengers = {
			...currentSearch.passengers,
			adults: adultsNew,
			teens: teensNew,
		};

		if (infants > maxInfantAmount(newPassengers)) {
			setIsWithTeenInfantRemoveWarning(true);
			return;
		}

		setIsWithTeenInfantRemoveWarning(false);
	};

	const increasePaxNumber = (type: PaxType, isDisabled: boolean) => {
		if (isDisabled) {
			checkForGroupBookingWarning(type);
			return;
		}

		setIsWithGroupBookingWarning(false);
		setIsWithTeenInfantRemoveWarning(false);

		setCurrentSearch({
			...currentSearch,
			passengers: { ...currentSearch.passengers, [type]: currentSearch.passengers[type] + 1 },
		});
	};

	const decreasePaxNumber = (type: PaxType, isDisabled: boolean) => {
		setIsWithGroupBookingWarning(false);
		checkForTeenInfantRemoveWarning(type);

		if (isDisabled) return;

		setCurrentSearch({
			...currentSearch,
			passengers: { ...currentSearch.passengers, [type]: currentSearch.passengers[type] - 1 },
		});
	};

	useEffectWithoutCleanup(() => {
		const newPassengers = { ...currentSearch.passengers };

		if (newPassengers.infants > maxInfantAmount(newPassengers)) {
			newPassengers.infants = maxInfantAmount(newPassengers);
		}
		if (newPassengers.children > maxChildrenAmount(newPassengers)) {
			newPassengers.children = maxChildrenAmount(newPassengers);
		}

		if (
			newPassengers.infants !== currentSearch.passengers.infants ||
			newPassengers.children !== currentSearch.passengers.children
		) {
			setCurrentSearch({ ...currentSearch, passengers: newPassengers });
		}
	}, [adults, teens, children, infants]);

	useEffectWithoutCleanup(() => {
		const newWarnings: WarningKey[] = [];

		if (isWithGroupBookingWarning) newWarnings.push("groupBooking");

		if (isWithTeenInfantRemoveWarning) newWarnings.push("teenInfantRemove");

		if (children > 0 || infants > 0) newWarnings.push("minorOrInfantAge");

		if (children > 0 && isColombianDomesticFlight && props.culture.toLowerCase() === COLOMBIAN_CULTURE_CODE) {
			newWarnings.push("childrenAgeWarningColombia");
		}

		if (teens > 0) newWarnings.push("teenLegalGuardian");

		setWarningKeys(newWarnings);
	}, [currentSearch.passengers, isWithGroupBookingWarning, isWithTeenInfantRemoveWarning]);

	useEffectWithoutCleanup(() => {
		setIsWithGroupBookingWarning(false);
		setIsWithTeenInfantRemoveWarning(false);
	}, [isOpen]);

	useEffectWithoutCleanup(init, [isOpen]);

	const labelTemplate = (label: string) =>
		html`<div class="font-body text-base font-bold text-n-blue">${label}</div>`;

	const infoTemplate = (info: string) => html`<div class="font-body text-xs text-[#a1a1a1]">${info}</div>`;

	const amountTemplate = (amount: number) =>
		html`<div class="flex h-full w-full items-center justify-center font-body text-sm font-bold">${amount}</div>`;

	const adultsTemplate = () => html`
		<div class="mb-4 flex w-full items-center justify-between">
			<div>${labelTemplate(i18next.t("Adultos"))} ${infoTemplate(i18next.t("18 o más años"))}</div>
			<div class="grid grid-cols-[24px_24px_24px] grid-rows-[24px]">
				<ac-change-search-button
					.type=${"minus"}
					.isDisabled=${isRemoveAdultsDisabled()}
					.onClick=${() => decreasePaxNumber("adults", isRemoveAdultsDisabled())}
				></ac-change-search-button>
				${amountTemplate(adults)}
				<ac-change-search-button
					.type=${"plus"}
					.isDisabled=${isAddAdultsDisabled()}
					.onClick=${() => increasePaxNumber("adults", isAddAdultsDisabled())}
				></ac-change-search-button>
			</div>
		</div>
	`;

	const teensTemplate = () => html`
		<div class="mb-4 flex w-full items-center justify-between">
			<div>
				${labelTemplate(i18next.t("Adolescentes"))}
				${isChileanSearch() ? infoTemplate(i18next.t("14-17 años")) : infoTemplate(i18next.t("13-17 años"))}
			</div>
			<div class="grid grid-cols-[24px_24px_24px] grid-rows-[24px]">
				<ac-change-search-button
					.type=${"minus"}
					.isDisabled=${isRemoveTeensDisabled()}
					.onClick=${() => decreasePaxNumber("teens", isRemoveTeensDisabled())}
				></ac-change-search-button>
				${amountTemplate(teens)}
				<ac-change-search-button
					.type=${"plus"}
					.isDisabled=${isAddTeensDisabled()}
					.onClick=${() => increasePaxNumber("teens", isAddTeensDisabled())}
				></ac-change-search-button>
			</div>
		</div>
	`;

	const childrenTemplate = () => html`
		<div class="mb-4 flex w-full items-center justify-between">
			<div>
				${labelTemplate(i18next.t("Niños"))}
				${isChileanSearch() ? infoTemplate(i18next.t("2-13 años")) : infoTemplate(i18next.t("2-12 años"))}
			</div>
			<div class="grid grid-cols-[24px_24px_24px] grid-rows-[24px]">
				<ac-change-search-button
					.type=${"minus"}
					.isDisabled=${isRemoveChildrenDisabled()}
					.onClick=${() => decreasePaxNumber("children", isRemoveChildrenDisabled())}
				></ac-change-search-button>
				${amountTemplate(children)}
				<ac-change-search-button
					.type=${"plus"}
					.isDisabled=${isAddChildrenDisabled()}
					.onClick=${() => increasePaxNumber("children", isAddChildrenDisabled())}
				></ac-change-search-button>
			</div>
		</div>
	`;

	const infantsTemplate = () => html`
		<div class="mb-4 flex w-full items-center justify-between">
			<div>${labelTemplate(i18next.t("Infantes"))} ${infoTemplate(i18next.t("0-23 meses"))}</div>
			<div class="grid grid-cols-[24px_24px_24px] grid-rows-[24px]">
				<ac-change-search-button
					.type=${"minus"}
					.isDisabled=${isRemoveInfantsDisabled()}
					.onClick=${() => decreasePaxNumber("infants", isRemoveInfantsDisabled())}
				></ac-change-search-button>
				${amountTemplate(infants)}
				<ac-change-search-button
					.type=${"plus"}
					.isDisabled=${isAddInfantsDisabled()}
					.onClick=${() => increasePaxNumber("infants", isAddInfantsDisabled())}
				></ac-change-search-button>
			</div>
		</div>
	`;

	const submitTemplate = () => html`
		<div
			class=${classNames(
				"flex w-full cursor-pointer items-center justify-center rounded-full bg-n-cyan py-3 font-body text-sm font-semibold text-white",
				"md:hover:bg-n-blue"
			)}
			@click=${() => setIsOpen(false)}
		>
			<div class="flex items-center justify-center">
				${i18next.t("Confirmar")} <i class="jsh-circle-chevron-right1 ml-2 mt-px text-sm"></i>
			</div>
		</div>
	`;

	const warningsTemplate = () =>
		warningKeys.length > 0
			? html` <div class="mt-2 flex flex-col items-start">
					${warningKeys.map(
						(key) => html` <div class="mt-2 font-body text-xs text-n-blue">${warningsMap.get(key)}</div> `
					)}
			  </div>`
			: "";

	const dropdownTemplate = () => html`
		<div
			ref=${ref(dropdownElem)}
			class=${classNames(
				"max-h-[370px] w-[232px]  max-w-full flex-col overflow-y-scroll rounded-lg bg-white p-4",
				"md:max-h-full md:w-[272px] md:flex-row md:items-stretch md:overflow-hidden"
			)}
		>
			${adultsTemplate()} ${teensTemplate()} ${childrenTemplate()} ${infantsTemplate()} ${submitTemplate()}
			${warningsTemplate()}
		</div>
	`;

	const inputTemplate = () => html`
		<div
			class=${classNames(
				"flex h-[34px] w-full cursor-pointer items-center justify-between rounded-lg border border-solid bg-white px-4",
				"md:h-[42px]",
				CLASS_NAMES.FLIGHT_CHANGE_SEARCH_PASSENGER_SELECTOR_CLASS,
				{
					"border-n-cyan": isOpen,
					"border-n-blue": !isOpen,
				}
			)}
			@click=${() => setIsOpen(!isOpen)}
		>
			<div class="font-body text-base text-n-blue">
				${total} ${total === 1 ? i18next.t("pasajero") : i18next.t("pasajeros")}
			</div>
			<i class="jsh-user text-base text-[#c1c1c1]"></i>
		</div>
	`;

	const htmlTemplate = () => html`
		<div ref=${ref(root)} class="relative">
			${inputTemplate()}
			<ac-dropdown2 .content=${dropdownTemplate()} .isOpen=${isOpen}></ac-dropdown2>
		</div>
	`;

	return { htmlTemplate, open: () => setIsOpen(true) };
};
